import {Grid} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import {ClearSharp} from "@material-ui/icons";
import Joi from "joi";
import * as PropTypes from "prop-types";
import React, {useEffect, useState} from "react";
import {BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBB} from "../../../consts/funktionaleRollen";
import {getAnlagenByBetriebEndpoint} from "../../../services/AnlagenService";
import {useHttp} from "../../../services/HttpService";
import {logger} from "../../../services/LogService";
import {getAuflagenartEndpoint} from "../../../services/WertelistenService";
import {createCalculatedColumn, createColumn, createComponentColumn, createDateColumn} from "../../../utils/ColumnUtils";
import {formatDate} from "../../../utils/DateUtils";
import {useForm} from "../../../utils/FormUtils";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {useHttpSortById} from "../../../utils/usePreformattedHttp";
import {nullableString, required, requiredDate, requiredNumber, requiredObject, requiredWerteListeObjekt} from "../../../utils/Validator";
import EAAccordion from "../../EAAccordion";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADialog from "../../EADialog";
import EATable from "../../EATable";
import EATextField from "../../EATextField";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import {useConfirmation} from "../../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../../ConfirmationDialog/confirmationDialogOptions";

function AnlageDialog(props) {
    let [{data, loading}] = useHttp({
        url: getAnlagenByBetriebEndpoint(props.betriebsId),
        timeout: 600000,
    });
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return !filter || item.betrieb.value.includes(filter.toLowerCase()) || item.bezeichnung.includes(filter.toLowerCase());
                }),
            ]);
        }
    }, [data, filter, loading]);

    return (
        <EADialog
            open={props.open}
            titleChildren={"Anlage auswählen"}
            content={
                <div style={{width: "700px", maxHeight: "520px", minHeight: "400px"}}>
                    <div className="gridl" style={{gridTemplateColumns: "repeat(3,auto)", paddingBottom: "8px"}}>
                        <EATextField
                            value={filter}
                            onChange={(e) => {
                                e.preventDefault();
                                setFilter(e.target.value);
                            }}
                            id="aufnahmeFilter"
                            data-cy="aufnahmeFilter"
                            label="Suchkriterium"
                        />
                        <div>
                            <EAButton
                                onClick={() => {
                                    setFilter("");
                                }}
                                id="aufnahmeFilterSucheReset"
                                data-cy="aufnahmeFilterSucheReset"
                                label="Zurücksetzen"
                            />
                        </div>
                    </div>
                    <EATable
                        id="aufnahmeAuswahl"
                        initialSortColumn="betrieb"
                        columns={[
                            createComponentColumn(
                                "actions",
                                (row, index) => {
                                    return (
                                        <Grid container spacing={1} justify="center">
                                            <Grid item>
                                                <EATableActionButton id={"selectAufnahme-" + index} iconName="ico-check">
                                                    <span
                                                        onClick={() => {
                                                            setFilter("");
                                                            props.onClickOk(row);
                                                        }}
                                                        className={"w-100"}
                                                        style={{
                                                            height: "100%",
                                                            display: "block",
                                                        }}
                                                    />
                                                </EATableActionButton>
                                            </Grid>
                                        </Grid>
                                    );
                                },
                                "",
                                null,
                                {width: "50px", padding: "0"}
                            ),
                            createCalculatedColumn(
                                "betrieb",
                                (row) => {
                                    return row.betrieb.value;
                                },
                                "Betrieb"
                            ),
                            createColumn("bezeichnung", "Abwasseranlage"),
                        ]}
                        data={filteredData}
                        loading={loading}
                    />
                </div>
            }
            buttons={
                <EAButtonBar>
                    <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                </EAButtonBar>
            }
        />
    );
}

AnlageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

function SingleAuflageDialog({open, data, onClickCancel, onClickOk, abwasseranlage, title}) {
    const [auflageOptionen] = useHttpSortById(getAuflagenartEndpoint);
    const schema = {beschreibung: nullableString(), art: requiredWerteListeObjekt(), fristdatum: requiredDate()};
    const {values, setValues, formFactory, validate} = useForm({
        initialValues: {data},
        schema,
    });

    useEffect(() => {
        setValues(data);
    }, [data, setValues]);
    return (
        <React.Fragment>
            <EADialog
                open={open}
                titleChildren={`Auflage für ${abwasseranlage?.bezeichnung ? abwasseranlage?.bezeichnung : abwasseranlage?.value} ${title}`}
                content={
                    <div style={{width: "600px"}}>
                        <div className="gridl" style={{gridTemplateColumns: "repeat(2,minmax(max-content,1fr))"}}>
                            {formFactory.renderDatePicker("fristdatum", "fristdatum", "Fristdatum", false)}
                            {formFactory.renderAutocompleteWerteliste("art", "art", "Art der Auflage", auflageOptionen)}
                            {formFactory.renderInput("beschreibung", "beschreibung", "Beschreibung", "text", false, {
                                multiline: true,
                                rows: 10,
                                style: {gridColumnEnd: "span 2"},
                            })}
                        </div>
                    </div>
                }
                buttons={
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel}/>
                        <EAButton
                            id={"btnOk"}
                            data-cy={"btnOk"}
                            color="primary"
                            label={"Übernehmen"}
                            onClick={() => {
                                const e = validate();
                                if (e && Object.keys(e).length > 0) {
                                    return;
                                }
                                onClickOk(values);
                            }}
                        />
                    </EAButtonBar>
                }
                closeFunction={onClickCancel}
            />
        </React.Fragment>
    );
}

SingleAuflageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    abwasseranlage: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
    title: PropTypes.oneOf(["bearbeiten", "hinzufügen"]).isRequired,
};

function AuflagenDialog({open, onClickCancel, onClickOk, modus, betriebsId}) {
    const [anlageDlgOpen, setAnlageDlgOpen] = useState(false);
    const [auflageDlgOpen, setAuflageDlgOpen] = useState(false);
    const [tableIndex, setTableIndex] = useState(-1);
    const schema = {
        abwasseranlage: requiredObject({id: requiredNumber()}),
        singleAuflagen: Joi.array()
            .items(requiredObject({beschreibung: nullableString(), art: requiredWerteListeObjekt(), fristdatum: requiredDate()}))
            .error((errors) => {
                errors[0].message = "Es muss mindestens eine Auflage angegeben werden.";
                return errors[0];
            }),
    };
    const {values, setValues, setErrors, formFactory, validate, validateDirectly} = useForm({
        initialValues: {
            abwasseranlage: null,
            singleAuflagen: [],
        },
        schema,
    });

    function handleAnlageClose() {
        setAnlageDlgOpen(false);
    }

    function handleAnlageCloseOk(abwasseranlage) {
        setAnlageDlgOpen(false);
        const valuesToSet = {...values};
        valuesToSet.abwasseranlage = abwasseranlage;
        setValues(valuesToSet);
        setErrors(validateDirectly(valuesToSet));
    }

    function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        //kein Warnhinweis hier, da es innerhalb eines Auswahldialogs eher stört als sinnvoll zu sein.

        const valuesToSet = {...values};
        valuesToSet.singleAuflagen.splice(index, 1);
        setValues(valuesToSet);
        setErrors(validateDirectly(valuesToSet));
    }

    function handleEdit(index) {
        if (isDisabled()) {
            return;
        }
        setTableIndex(index);
        setAuflageDlgOpen(true);
    }

    function handleClose() {
        setAuflageDlgOpen(false);
        setTableIndex(-1);
    }

    function handleCloseOk(auflage) {
        setAuflageDlgOpen(false);
        delete auflage.data;
        const valuesToSet = {...values};
        if (tableIndex === -1) {
            valuesToSet.singleAuflagen.push(auflage);
        } else {
            valuesToSet.singleAuflagen[tableIndex] = {...valuesToSet.singleAuflagen[tableIndex], ...auflage};
        }
        setValues(valuesToSet);
        setTableIndex(-1);
        setErrors(validateDirectly(valuesToSet));
    }

    function isDisabled() {
        if (modus === "l") {
            return true;
        }
        if (modus === "s") {
            return !userHasAnyRole([BETREIBER, BEAUFTRAGTER, FACHADMIN, SYSTEMADMIN, ZUST_SBB]);
        }
        return true;
    }

    return (
        <React.Fragment>
            {anlageDlgOpen && (
                <AnlageDialog
                    open={anlageDlgOpen}
                    betriebsId={betriebsId}
                    onClickCancel={handleAnlageClose}
                    onClickOk={handleAnlageCloseOk}
                />
            )}
            {auflageDlgOpen && (
                <SingleAuflageDialog
                    open={auflageDlgOpen}
                    data={tableIndex !== -1 ? {...values.singleAuflagen[tableIndex]} : {...{}}}
                    title={tableIndex !== -1 ? "bearbeiten" : "hinzufügen"}
                    abwasseranlage={values.abwasseranlage}
                    formFactory={formFactory}
                    onClickCancel={handleClose}
                    onClickOk={handleCloseOk}
                />
            )}
            <EADialog
                open={open}
                titleChildren={"Auflagen " + (values.abwasseranlage ? `für ${values.abwasseranlage.bezeichnung}` : "")}
                content={
                    <div style={{width: "600px"}}>
                        <div className="gridl" style={{gridTemplateColumns: "repeat(2,minmax(max-content,1fr))"}}>
                            <div className="gridl" style={{gridColumnEnd: "span 2", gridTemplateColumns: "1fr auto"}}>
                                {formFactory.renderInput(
                                    "abwasseranlage.bezeichnung",
                                    "abwasseranlage.bezeichnung",
                                    "Anlage",
                                    "text",
                                    true,
                                    {
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ClearSharp
                                                        onClick={() => {
                                                            const valuesToSet = {...values};
                                                            valuesToSet.abwasseranlage = null;
                                                            setValues(valuesToSet);
                                                            setErrors(validateDirectly(valuesToSet));
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        },
                                    }
                                )}
                                <div>
                                    {formFactory.renderButton(
                                        "anlageAendern",
                                        "anlageAendern",
                                        "auswählen...",
                                        () => setAnlageDlgOpen(true),
                                        {
                                            "data-cy": "anlageAendern",
                                        }
                                    )}
                                </div>
                                {formFactory.renderError("abwasseranlage", "abwasseranlage")}
                            </div>
                            <EATable
                                id="singleAuflagen"
                                initialSortColumn="fristdatum"
                                columns={[
                                    createComponentColumn(
                                        "actions",
                                        (row, index) => {
                                            if (isDisabled()) return <div/>;
                                            return (
                                                <Grid container spacing={1} justify="center">
                                                    <Grid item>
                                                        <EATableActionButton id={"edit-" + index} iconName="ico-edit">
                                                            <span
                                                                onClick={() => handleEdit(index)}
                                                                className={"w-100"}
                                                                style={{
                                                                    height: "100%",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        </EATableActionButton>
                                                    </Grid>
                                                    <Grid item>
                                                        <EATableActionButton id={"delete-" + index} iconName="ico-delete">
                                                            <span
                                                                onClick={() => handleDelete(index)}
                                                                className={"w-100"}
                                                                style={{
                                                                    height: "100%",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        </EATableActionButton>
                                                    </Grid>
                                                </Grid>
                                            );
                                        },
                                        "",
                                        null,
                                        {width: "50px", padding: "0"}
                                    ),
                                    createCalculatedColumn(
                                        "fristdatum",
                                        (row) => {
                                            return row.fristdatum ? formatDate(row.fristdatum) : "";
                                        },
                                        "Fristdatum"
                                    ),
                                    createCalculatedColumn(
                                        "art",
                                        (row) => {
                                            return row.art?.value;
                                        },
                                        "Art der Auflage",
                                        {padding: "0"}
                                    ),
                                    createColumn("beschreibung", "Beschreibung", {padding: "0"}),
                                ]}
                                data={[...values.singleAuflagen]}
                                style={{gridColumnEnd: "span 2"}}
                            />
                            {formFactory.renderError("singleAuflagen", "auflagen-helper-text")}
                            {formFactory.renderButton(
                                "addAuflage",
                                "addAuflage",
                                "Hinzufügen",
                                () => {
                                    setTableIndex(-1);
                                    setAuflageDlgOpen(true);
                                },
                                {
                                    style: {gridColumnEnd: "span 2", placeSelf: "end"},
                                    disabled: !values.abwasseranlage,
                                }
                            )}
                        </div>
                    </div>
                }
                buttons={
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel}/>
                        <EAButton
                            id={"btnOk"}
                            data-cy={"btnOk"}
                            color="primary"
                            label={"Übernehmen"}
                            onClick={() => {
                                const e = validate();
                                if (e && Object.keys(e).length > 0) {
                                    return;
                                }
                                const valuesToSet = {...values};
                                onClickOk(
                                    valuesToSet.singleAuflagen.map((singleAuflage) => {
                                        singleAuflage.abwasseranlage = values.abwasseranlage;
                                        return singleAuflage;
                                    })
                                );
                            }}
                        />
                    </EAButtonBar>
                }
                closeFunction={onClickCancel}
            />
        </React.Fragment>
    );
}

AuflagenDialog.propTypes = {
    open: PropTypes.bool,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
    modus: PropTypes.string.isRequired,
};

export default function Auflagen({data, formFactory: outerFormFactory, onChange, betriebsId, modus, isFreigegeben}) {
    const confirm = useConfirmation();
    const dataName = "auflagen";
    const [tableIndex, setTableIndex] = useState(-1);
    const {values, setValues, errors, formFactory} = useForm({
        initialValues: {
            abwasseranlage: null,
            beschreibung: "",
            art: null,
            fristdatum: null,
        },
        schema: {
            abwasseranlage: required(Joi.any()),
            beschreibung: nullableString(),
            art: requiredWerteListeObjekt(),
            fristdatum: requiredDate(),
        },
    });
    const [auflagenDlgOpen, setAuflagenDlgOpen] = useState(false);
    const [singleAuflageDlgOpen, setSingleAuflageDlgOpen] = useState(false);

    function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        confirm(
            confirmDeleteOption(
                "die Auflage",
                () => removeAuflage(index),
                () => {
                }
            )
        );
    }

    function removeAuflage(index) {
        data.splice(index, 1);
        setValues({});
        onChange(dataName, data);
    }

    function handleEdit(index) {
        logger.debug("Edit " + dataName);
        if (isDisabled()) {
            return;
        }
        setTableIndex(index);
        setValues(data[index]);
        setSingleAuflageDlgOpen(true);
    }

    function handleClose() {
        setSingleAuflageDlgOpen(false);
        setTableIndex(-1);
        setValues({});
    }

    function handleCloseOk(auflage) {
        setSingleAuflageDlgOpen(false);
        data[tableIndex] = {...data[tableIndex], ...auflage};
        logger.debug(dataName + "Close", data);
        setTableIndex(-1);
        setValues({});
        onChange(dataName, data);
    }

    function handleAuflagenClose() {
        setAuflagenDlgOpen(false);
        setTableIndex(-1);
        setValues({});
    }

    function handleAuflagenCloseOk(auflagen) {
        setAuflagenDlgOpen(false);
        auflagen.forEach((auflage) => data.push(auflage));
        logger.debug(dataName + "Close", data);
        setTableIndex(-1);
        setValues({});
        onChange(dataName, data);
    }

    function isDisabled() {
        if (modus === "l") {
            return true;
        }
        if (isFreigegeben) {
            return true;
        }
        if (modus === "s") {
            return !userHasAnyRole([BETREIBER, BEAUFTRAGTER, FACHADMIN, SYSTEMADMIN, ZUST_SBB]);
        }
        return false;
    }

    return (
        <div className="tabsheet-content-margin">
            {auflagenDlgOpen && (
                <AuflagenDialog
                    formFactory={formFactory}
                    betriebsId={betriebsId}
                    open={auflagenDlgOpen}
                    onClickCancel={handleAuflagenClose}
                    data={values}
                    errors={errors}
                    setData={setValues}
                    onClickOk={handleAuflagenCloseOk}
                    modus={modus}
                />
            )}
            {singleAuflageDlgOpen && (
                <SingleAuflageDialog
                    data={data[tableIndex]}
                    open={singleAuflageDlgOpen}
                    title={"bearbeiten"}
                    abwasseranlage={data[tableIndex].abwasseranlage}
                    onClickCancel={handleClose}
                    onClickOk={handleCloseOk}
                />
            )}
            <EAAccordion
                label={`Auflagen (${data ? data.length : 0})`}
                className="gridl"
                expandIcon={null}
                id="auflagenAccordion"
                variant="light"
                expanded={true}
            >
                <EATable
                    id="auflagen"
                    initialSortColumn="abwasseranlage"
                    columns={[
                        createComponentColumn(
                            "actions",
                            (row, index) => {
                                if (isDisabled() || outerFormFactory.getReadonly()) return <div/>;
                                return (
                                    <Grid container spacing={1} justify="center">
                                        <Grid item>
                                            <EATableActionButton id={"edit-" + index} iconName="ico-edit">
                                                <span
                                                    onClick={() => handleEdit(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                        <Grid item>
                                            <EATableActionButton id={"delete-" + index} iconName="ico-delete">
                                                <span
                                                    onClick={() => handleDelete(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                    </Grid>
                                );
                            },
                            "",
                            null,
                            {width: "50px", padding: "0"}
                        ),
                        createCalculatedColumn(
                            "abwasseranlage",
                            (row) => {
                                return row.abwasseranlage?.bezeichnung ? row.abwasseranlage?.bezeichnung : row.abwasseranlage?.value;
                            },
                            "Abwasseranlage"
                        ),
                        createDateColumn("fristdatum", "Fristdatum"),
                        createCalculatedColumn(
                            "art",
                            (row) => {
                                return row.art?.value;
                            },
                            "Art der Auflage",
                            {padding: "0"}
                        ),
                        createColumn("beschreibung", "Beschreibung", {padding: "0"}),
                    ]}
                    data={[...data]}
                />
                {outerFormFactory.renderError("auflagen", "auflagen-helper-text")}
                {!isDisabled() && outerFormFactory.renderButton(
                    "addAuflagen",
                    "addAuflagen",
                    "Hinzufügen",
                    () => {
                        setTableIndex(-1);
                        setAuflagenDlgOpen(true);
                    },
                    {
                        style: {placeSelf: "end"},
                    }
                )}
            </EAAccordion>
            <div style={{paddingTop: "4px"}}/>
        </div>
    );
}

Auflagen.propTypes = {
    data: PropTypes.array.isRequired,
    formFactory: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    betriebsId: PropTypes.string.isRequired,
    modus: PropTypes.string.isRequired,
};
